import { type FieldStat } from '@solink/vd-data-layer/api-clients/platform/platform';
import { type EventStandardDefs } from '@solinkcorp/event-standard';
import { type LocaleDefinition } from '@solinkcorp/locale';
import { getLocalTimeZone } from '@solinkcorp/utils';
import { type SearchOperator } from '@solinkcorp/vd-common';
import { DAWN_OF_TIME } from 'clients/EventClient';
import capitalize from 'lodash/capitalize';
import groupBy from 'lodash/groupBy';
import memoizeOne from 'memoize-one';
import moment from 'moment';
import { type FieldStats, getNoPrefixFieldsDict } from 'reducers/fieldStatsReducer';
import { dateTimeFormatMMMDDYYYY } from 'src/core/utils/timeFormat';
import CommonUtil from 'utils/CommonUtil';
import { parseSearchCapsule } from 'utils/EventsFetchUtil';
import * as EventUtil from 'utils/EventUtil';
import type ReportUtil from 'utils/ReportUtil';
import { type StringMap } from 'utils/types';
import { DATA_CATEGORY_ALL, DATA_CATEGORY_OTHER } from 'vd-constants';
import XRegExp from 'xregexp';
export const SPACE = '&nbsp;';
export const START_END_DATE_SEPARATOR = ' - ';
export const UNIX_TIMESTAMPS_INDEX = 2;

const regexNonUnicodeWordEnding = XRegExp('[^\\pL\\pN]+$');
const regexUnicodeWord = XRegExp('^[\\pL\\pN]+$');

export enum EventStandardFieldType {
  DATE_TIME = 'date-time',
  NUMBER = 'number',
  STRING = 'string',
  ENUM = 'enum',
  BOOLEAN = 'boolean',
  OBJECT = 'object',
  FREEFORM = 'freeform',
  AGENT_OUTPUT = 'agent-output',
}

export enum FieldStatFieldType {
  FLOAT = 'float',
  DOUBLE = 'double',
  INTEGER = 'integer',
  DATE = 'date',
  LONG = 'long',
}

export enum QueryType {
  FIELD = 'field',
  STRING = 'string',
  EVENT = 'event',
}

export enum FilterOperator {
  IS = 'is',
  IS_NOT = 'isNot',
  CONTAINS = 'contains',
  DOES_NOT_CONTAIN = 'doesNotContain',
  SINCE = 'gte',
  ON = 'on',
  BEFORE = 'lt',
  BETWEEN = 'between',
}

export interface DataFilterFieldStat extends FieldStat {
  rawName: string;
  categories: string[];
  type: EventStandardFieldType;
  hasKeyword?: boolean;
  /** force contains search for fields with type: 'string' */
  forceSearchable?: boolean;
}

export interface StringQuery {
  type: typeof QueryType.STRING;
  query: string;
  value: string;
  operator: string;
  index?: number;
}

export interface DataFieldQuery {
  type: typeof QueryType.FIELD;
  query: string;
  rawName: string;
  operator: string;
  value: string;
  index?: number;
}

export interface EventTypeQuery {
  type: typeof QueryType.EVENT;
  operator: string;
  query: string;
  value: string;
  index?: number;
  isSubtype?: boolean;
}

export type FilterQueryType = DataFieldQuery | StringQuery | EventTypeQuery;
/**
 * Map the fieldstats field type to event standard field type
 */
const typeMap: StringMap = {
  [FieldStatFieldType.FLOAT]: EventStandardFieldType.NUMBER,
  [FieldStatFieldType.DOUBLE]: EventStandardFieldType.NUMBER,
  [FieldStatFieldType.INTEGER]: EventStandardFieldType.NUMBER,
  [FieldStatFieldType.LONG]: EventStandardFieldType.NUMBER,
  [FieldStatFieldType.DATE]: EventStandardFieldType.DATE_TIME,
};

export const SUPPORTING_FIELD_TYPES = [
  EventStandardFieldType.NUMBER,
  EventStandardFieldType.STRING,
  EventStandardFieldType.ENUM,
  EventStandardFieldType.BOOLEAN,
  EventStandardFieldType.DATE_TIME,
  EventStandardFieldType.FREEFORM,
  EventStandardFieldType.AGENT_OUTPUT,
];

export const getOperatorMap = (
  locale: LocaleDefinition,
  type: EventStandardFieldType | 'freeform',
) => {
  const map: { [key: string]: any } = {
    [EventStandardFieldType.NUMBER]: {
      '>': locale.general.greaterThan,
      '<': locale.general.lessThan,
      '=': locale.general.equalTo,
      '<=': locale.general.equalOrLessThan,
      '>=': locale.general.equalOrGreaterThan,
    },
    [EventStandardFieldType.STRING]: {
      [FilterOperator.IS]: locale.general.is,
      [FilterOperator.IS_NOT]: locale.general.isNot,
      [FilterOperator.CONTAINS]: locale.general.contains,
      [FilterOperator.DOES_NOT_CONTAIN]: locale.general.doesNotContain,
    },
    [EventStandardFieldType.ENUM]: {
      [FilterOperator.IS]: locale.general.is,
      [FilterOperator.IS_NOT]: locale.general.isNot,
    },
    [EventStandardFieldType.BOOLEAN]: {
      [FilterOperator.IS]: locale.general.is,
    },
    [EventStandardFieldType.DATE_TIME]: {
      [FilterOperator.BETWEEN]: locale.general.between,
      [FilterOperator.ON]: locale.general.onDate,
    },
    freeform: {
      [FilterOperator.CONTAINS]: locale.general.contains,
      [FilterOperator.DOES_NOT_CONTAIN]: locale.general.doesNotContain,
    },
    [EventStandardFieldType.AGENT_OUTPUT]: {
      [FilterOperator.CONTAINS]: locale.general.contains,
      [FilterOperator.DOES_NOT_CONTAIN]: locale.general.doesNotContain,
    },
  };

  return map[type] || {};
};

export const getAllOperatorMap = (locale: LocaleDefinition) => {
  let map = {};
  SUPPORTING_FIELD_TYPES.forEach((type) => {
    map = {
      ...map,
      ...getOperatorMap(locale, type),
    };
  });
  return {
    ...map,
    ...getOperatorMap(locale, 'freeform'),
  };
};

export const processFieldStats = memoizeOne(
  (
    eventStandard: EventStandardDefs,
    rawFields: FieldStats,
    localeCode: string,
  ): { fields: DataFilterFieldStat[]; fieldNameMap: StringMap } => {
    const fieldNameMap: StringMap = {};
    const newFields: DataFilterFieldStat[] = [];
    rawFields.forEach((originalField) => {
      const field = {
        ...originalField,
        rawName: originalField.name,
      } as DataFilterFieldStat;

      const fieldRawName = field.rawName.replace('.keyword', '');
      const eventStandardName = EventUtil.i18nEventFieldName(
        fieldRawName,
        true,
        eventStandard,
        localeCode,
      );
      // Convert raw name to a user friendly name, if the field does not exist in event standard
      const friendlyName = /\./.test(eventStandardName)
        ? EventUtil.getFriendlyFieldName(eventStandardName)
        : eventStandardName;

      // The name map keys are raw names with prefix like 'details.' and suffix like '.keyword'
      fieldNameMap[originalField.name] = friendlyName;

      const eventStandardField = eventStandard[fieldRawName];
      field.name = friendlyName;
      field.categories = eventStandardField?.categories || [DATA_CATEGORY_OTHER];
      field.type =
        (eventStandardField?.type as EventStandardFieldType) || typeMap[field.type] || field.type;

      field.hasKeyword = eventStandardField?.hasKeyword || originalField.name.includes('.keyword');
      field.forceSearchable = eventStandardField?.features?.forceContains ?? false;

      // Field-stats often type nested maps as object; filtering uses the dedicated Agent Output UI only for this path.
      if (fieldRawName === 'details.agentOutputs') {
        field.type = EventStandardFieldType.AGENT_OUTPUT;
      }

      if (SUPPORTING_FIELD_TYPES.includes(field.type) && !eventStandardField?.disabled) {
        newFields.push(field);
      }
    });

    newFields.sort((a, b) => CommonUtil.sortObjectAlphabetically(a, b, 'name'));
    return { fields: newFields, fieldNameMap };
  },
);

export const processCategories = memoizeOne(
  (
    eventStandard: EventStandardDefs,
    fields: DataFilterFieldStat[],
    locale: LocaleDefinition,
    menuAction: (category: string) => void,
  ) => {
    const categoryNameMap: StringMap = {
      [DATA_CATEGORY_ALL]: locale.general.allCategories,
      [DATA_CATEGORY_OTHER]: locale.general.other,
    };
    const categoryFieldsMap: { [key: string]: DataFilterFieldStat[] } = {
      [DATA_CATEGORY_ALL]: fields,
    };
    const categoryMenuItems: {
      text: string;
      type: string;
      onClick: () => void;
    }[] = [];

    fields.forEach((field: DataFilterFieldStat) => {
      const { categories = [] } = field;
      categories.forEach((category) => {
        if (!Object.hasOwnProperty.call(categoryNameMap, category)) {
          const friendlyName = capitalize(
            EventUtil.i18nEventFieldCategoryName(
              field.rawName,
              category,
              eventStandard,
              locale.locale,
            ),
          );
          categoryNameMap[category] = friendlyName;
          categoryMenuItems.push({
            text: friendlyName,
            type: 'action',
            onClick: () => menuAction(category),
          });
        }
        if (!Object.hasOwnProperty.call(categoryFieldsMap, category)) {
          categoryFieldsMap[category] = [field];
        } else {
          categoryFieldsMap[category].push(field);
        }
      });
    });

    categoryMenuItems.sort((a, b) => CommonUtil.sortObjectAlphabetically(a, b, 'text'));
    // Add the 'All categories' option to the beginning of the menu items array
    categoryMenuItems.unshift({
      text: categoryNameMap.all,
      type: 'action',
      onClick: () => menuAction(DATA_CATEGORY_ALL),
    });
    // Add the 'Other' option to the end of the menu items array
    if (
      categoryFieldsMap[DATA_CATEGORY_OTHER] &&
      categoryFieldsMap[DATA_CATEGORY_OTHER].length > 0
    ) {
      categoryMenuItems.push({
        text: categoryNameMap.other,
        type: 'action',
        onClick: () => menuAction(DATA_CATEGORY_OTHER),
      });
    } else {
      delete categoryNameMap[DATA_CATEGORY_OTHER];
    }
    return { categoryMenuItems, categoryFieldsMap, categoryNameMap };
  },
);

/** Optional 4th segment (URI-encoded) is UI display only; segment 3 is the value used for ES. */
export const searchCapsulesToFilterQueries = (fieldStats: FieldStats, searchCapsules: string[]) => {
  const noPrefixFieldsDict = getNoPrefixFieldsDict(fieldStats);

  return searchCapsules.map((capsule: string) => {
    const [fieldName, operator, value, displayLabel] = capsule.split(SPACE);

    return {
      type: QueryType.FIELD,
      query: capsule,
      // TODO: change how we process the field name after we get rid of the legacy Search and FSSearch
      rawName: noPrefixFieldsDict[fieldName] ? fieldName : `details.${fieldName}`,
      operator,
      value: displayLabel ?? value,
    };
  });
};

export const searchStringToFilterQueries = ({
  searchString,
  searchOperator,
}: {
  searchString?: string;
  searchOperator: SearchOperator;
}): StringQuery[] => {
  let queries: StringQuery[] = [];
  if (searchString) {
    queries = searchString.split(` ${searchOperator} `).map((searchTerm) => {
      const sanitizedTerm = sanitizeSearchString(searchTerm);
      const displayString = sanitizedTerm.replace(/NOT\s/, '');

      return {
        type: QueryType.STRING,
        query: searchTerm,
        value: displayString,
        operator: /NOT\s/.test(sanitizedTerm)
          ? FilterOperator.DOES_NOT_CONTAIN
          : FilterOperator.CONTAINS,
      };
    });
  }
  return queries;
};

export function sanitizeSearchString(value: string) {
  return (
    value
      // Remove starting and ending double quotes (legacy implementation where users had to manually add double-quotes)
      .replace(/^"(.*)([^\\])"$/, '$1$2')
      // Strip wild card
      .replace(/\*$/, '')
      // Strip starting and ending bracket (only for OR precedence usage)
      .replace(/^\((.*)([^\\])\)$/, '$1$2')
      // Remove escape character if present
      .replace(/\\(\+|-|=|&{2}|\|{2}|!|\(|\)|\{|\}|\[|\]|\^|"|~|\*|\?|:|\\|\/)/g, '$1')
  );
}

export const processFreeformInput = (searchString: string) => {
  // The searchString can be a single string,
  // string with OR, like "timbits OR smoothie",
  // or string with NOT, like "NOT visa".
  const query = searchString
    .split(/(\sOR\s|NOT\s)/)
    .map((value) => {
      if (!value) {
        return;
      }
      if (/^(\sOR\s|NOT\s)$/.test(value)) {
        return value;
      }
      const sanitizedValue = value
        // Remove starting and ending double quotes (legacy implementation where users had to manually add double-quotes)
        .replace(/^"(.*)([^\\])"$/, '$1$2')
        // Remove these characters for ES: > <
        .replace(/[><]/g, '')
        // Escape these characters for ES: + - = && || ! ( ) { } [ ] ^ " ~ * ? : \ /
        .replace(/(\+|-|=|&{2}|\|{2}|!|\(|\)|\{|\}|\[|\]|\^|"|~|\*|\?|:|\\|\/)/g, '\\$1')
        // Strip any punctuation, special characters, and so on when at the end of the search term (for wild card appending)
        .replace(regexNonUnicodeWordEnding, '');

      // A search term is handled as a phrase if it contains a white space, punctuation, and so on
      if (regexUnicodeWord.test(sanitizedValue)) {
        // Add wildcard when not a phrase (a bit of hack until we implement a better solution)
        return `${sanitizedValue}*`;
      }
      // Wrap in double quotes for phrase matching
      return `"${sanitizedValue}"`;
    })
    .join('');

  return {
    query: /\sOR\s/.test(query) ? `(${query})` : query,
    displayString: searchString.replace(/NOT\s/, ''),
    operator: /NOT\s/.test(searchString)
      ? FilterOperator.DOES_NOT_CONTAIN
      : FilterOperator.CONTAINS,
  };
};

const isFieldDateTimeType = (
  eventStandard: EventStandardDefs,
  fieldStats: FieldStats,
  rawFieldName: string,
) => {
  return (
    eventStandard[rawFieldName.replace('.keyword', '')]?.type ===
      EventStandardFieldType.DATE_TIME ||
    fieldStats.find((stat: FieldStat) => stat.name === rawFieldName)?.type ===
      FieldStatFieldType.DATE
  );
};

export const areCapsulesFilteredByDateTimeField = memoizeOne(
  (eventStandard: EventStandardDefs, fieldStats: FieldStats, capsules: string[]) => {
    let result = false;
    if (capsules?.length > 0) {
      for (const capsule of capsules) {
        const filter = parseSearchCapsule(eventStandard, fieldStats, capsule, 'details.');
        if (filter && isFieldDateTimeType(eventStandard, fieldStats, filter.filterProp)) {
          result = true;
          break;
        }
      }
    }
    return result;
  },
);

export const getDateRangeFromFieldQueries = memoizeOne(
  (eventStandard: EventStandardDefs, fieldStats: FieldStats, fieldQueries: string[]) => {
    let start = DAWN_OF_TIME;
    let end = new Date().getTime() + 24 * 60 * 60 * 1000;
    let startQuery, endQuery;

    for (const query of fieldQueries) {
      const { filterProp, filterLogic, filterVal } =
        parseSearchCapsule(eventStandard, fieldStats, query, 'details.') || {};
      if (filterProp && isFieldDateTimeType(eventStandard, fieldStats, filterProp)) {
        if (
          filterLogic === FilterOperator.SINCE &&
          typeof filterVal === 'string' &&
          new Date(filterVal).getTime() > start
        ) {
          start = new Date(filterVal).getTime();
          startQuery = query;
        } else if (
          filterLogic === FilterOperator.BEFORE &&
          typeof filterVal === 'string' &&
          new Date(filterVal).getTime() < end
        ) {
          end = new Date(filterVal).getTime();
          endQuery = query;
        } else if (
          (filterLogic === FilterOperator.BETWEEN || filterLogic === FilterOperator.ON) &&
          typeof filterVal === 'object'
        ) {
          const [betweenStart, betweenEnd] = filterVal;
          const adjustedQuery = `${query.split(/[;](?=\d)/)[0]};${betweenStart} - ${betweenEnd}`;
          if (typeof betweenStart === 'string' && moment(betweenStart).isAfter(start)) {
            start = new Date(betweenStart).getTime();
            startQuery = adjustedQuery;
          }
          if (typeof betweenEnd === 'string' && moment(betweenEnd).isBefore(end)) {
            end = new Date(betweenEnd).getTime();
            endQuery = adjustedQuery;
          }
        }
      }
    }

    return { start, end, startQuery, endQuery };
  },
);

/**
 * Format the date time value for the display of event filter item
 */
export const formatTimeFilterDisplayValue = (
  {
    rawValue,
    operator,
  }: {
    rawValue: string;
    operator: string;
  },
  timezoneId = getLocalTimeZone(),
) => {
  // timestamps in rawValue are in browser's timezone and are only timezone adjusted when `events/query`
  // and `histogram` API calls are made. Format without 'z` and append timezoneAbbr
  const [startTime, endTime] = rawValue.split(START_END_DATE_SEPARATOR);

  if (operator === FilterOperator.BETWEEN) {
    return [startTime, endTime]
      .map((timestamp) =>
        dateTimeFormatMMMDDYYYY(parseInt(timestamp, 10), { timeZone: timezoneId }),
      )
      .join(START_END_DATE_SEPARATOR);
  } else if (operator === FilterOperator.ON) {
    return dateTimeFormatMMMDDYYYY(parseInt(startTime, 10), { timeZone: timezoneId });
  }
  return dateTimeFormatMMMDDYYYY(parseInt(startTime, 10), { timeZone: timezoneId });
};

/**
 * Checks if the report name is unique within a list of reports
 * Uniqueness is dependent on the user's locale
 */
export function isReportNameUnique({
  localeCode,
  reportName,
  reports,
}: {
  localeCode: string;
  reportName: string;
  reports: ReportUtil[];
}) {
  for (const report of reports) {
    if (report.getName(localeCode) === reportName) {
      return false;
    }
  }
  return true;
}

export function getFilterQueryDetails({
  fieldNameMap,
  operatorMap,
  filter,
  timezoneId,
}: {
  fieldNameMap: StringMap;
  operatorMap: StringMap;
  filter: FilterQueryType;
  timezoneId: string;
}) {
  // If the field name is not in the fieldNameMap, use the friendly field name
  const filterName = checkIsDataFieldQuery(filter)
    ? fieldNameMap[filter.rawName] || EventUtil.getFriendlyFieldName(filter.rawName)
    : '';
  const operator = operatorMap[filter.operator];
  const rawValue = filter.query.split(SPACE)[UNIX_TIMESTAMPS_INDEX];
  let value = filter.value;

  if (operator === FilterOperator.BETWEEN || operator === FilterOperator.ON) {
    value = formatTimeFilterDisplayValue({ rawValue, operator }, timezoneId);
  } else if (
    filter.operator === FilterOperator.CONTAINS ||
    filter.operator === FilterOperator.DOES_NOT_CONTAIN
  ) {
    const searchString =
      filter.operator === FilterOperator.DOES_NOT_CONTAIN ? value.replace(/NOT\s/, '') : value;
    value = sanitizeSearchString(searchString);
  }

  return { filterName, operator, value };
}

export function checkIsDataFieldQuery(query: FilterQueryType): query is DataFieldQuery {
  return query.type === QueryType.FIELD;
}

/** Parse the filter queries to freeform search string query and data queries
 * that can be used to filter events from Elasitcsearch
 */
export function parseFilterQueries(
  filterQueries: FilterQueryType[],
  searchOperator: SearchOperator,
) {
  const byType: { [key: string]: FilterQueryType[] } = groupBy(filterQueries, 'type');
  const searchStrings = byType.string?.map((item: FilterQueryType) => item.query) || [];
  const dataQueries = byType.field?.map((item: FilterQueryType) => item.query) || [];

  return {
    searchStrings,
    searchStringQuery: searchStrings.join(` ${searchOperator} `),
    dataQueries,
  };
}
